import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Snackbar, CircularProgress, makeStyles, createStyles, Input, InputLabel, FormControl, OutlinedInput, FormHelperText } from '@material-ui/core';
import MaskedInput from 'react-text-mask';
import { newSubscription, updatePhoneNumber } from '../../api/subscription';
import { Alert } from '@material-ui/lab';
import { MembershipTrainerData } from '../../templates/trainer-membership';
import PhoneInput from 'react-phone-input-2'
import startsWith from 'lodash.startswith'

export type ContactData = {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
};

interface Props {
  onNext: (contactData: ContactData) => void;
  enablePreOrder?: boolean;
  trainerData: MembershipTrainerData;
  reenterPhone: boolean;
}

const useStyles= makeStyles(({palette, spacing, typography})=>createStyles({
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  root: {
    borderColor: 'rgba(0, 0, 0, 0.23)',
    color: palette.text.primary,
    '& .react-tel-input': {
      '& .special-label': {
        left: spacing(1),
        color: palette.text.secondary,
        ...typography.caption
      },
      '& .invalid-number-message': {
        left: spacing(1),
        color: palette.error.main,
        ...typography.caption
      },
      '& .form-control': {
        width: '100%',
      },
      '& .form-control:hover': {
        borderColor: palette.common.black,
      },
      '& .form-control:focus': {
        borderColor: palette.primary.main,
        '& .special-label': {
          color: palette.primary.main
        },
      },
    },
  }
}))

const nameRegex = /^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$/u

interface TextMaskCustomProps {
  inputRef: (ref: HTMLInputElement | null) => void;
}

function TextMaskCustom(props: TextMaskCustomProps) {
  const { inputRef, ...other } = props;

  return (
    <MaskedInput
      {...other}
      ref={(ref: any) => {
        inputRef(ref ? ref.inputElement : null);
      }}
      mask={['+',/\d/,'(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
      placeholderChar={'\u2000'}
      showMask
    />
  );
}

export const ContactInfoStepContent: React.FC<Props> = ({ onNext, enablePreOrder=false, trainerData, reenterPhone }) => {
  const classes = useStyles()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [lastNameError, setLastNameError] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [validPhoneNumber, setValidPhoneNumber] = useState(true)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [phoneNumberError, setPhoneNumberError] = useState('')
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);
  const [error, setError] = useState(false);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNameError('');
    setName(event.target.value);
  };
  const validateName = () => {
    if (!name.trim()) {
      setNameError('Name is required');
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailError('');
    setEmail(event.target.value);
  };

  const validateEmail = () => {
    setEmailError('');
    if (!email.trim()) {
      return setEmailError('Email is required.');
    }
    if (!email.match(/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/)) {
      setEmailError(
        'Invalid email address. Check your spelling or try another one.'
      );
    }
  };

  const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFirstNameError('');
    setFirstName(event.target.value.trim());
  }

  const validateFirstName = () => {
    if (!firstName.trim()) {
      return setFirstNameError('First name is required');
    }
    if(!firstName.match(nameRegex)){
      setFirstNameError(
        'Invalid first name. Please check your spelling.'
      );
    }
  }
  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLastNameError('');
    setLastName(event.target.value.trim());
  }
  const validateLastName = () => {
    if (!lastName.trim()) {
      return setLastNameError('Last name is required');
    }
    if(!lastName.match(nameRegex)){
      setLastNameError(
        'Invalid last name. Please check your spelling.'
      );
    }
  }

  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumberError('')
    setPhoneNumber(value)
  }
  const validatePhoneNumber = () => {
    if (!phoneNumber.trim()) {
      return setPhoneNumberError('The phone is required');
    }
    if(!validPhoneNumber){
      return setPhoneNumberError('Invalid phone number');
    }
  }

  type PhoneInputCountry = {
    name: string,
    dialCode: string,
    countryCode: string
  }

  const updatePhone = async () => {
    const subscriptionId =  localStorage.getItem('clientSubscriptionId');
    try {
      if(subscriptionId){
        const res = await updatePhoneNumber({client_phone: phoneNumber, subscription_id: subscriptionId})
        const resData = await res.json()
        if(resData.status === 'Phone verification sent'){
          setLoading(false)
          return onNext({ firstName, lastName, phoneNumber, email })
        }
      }
      setError(true)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      setError(true);
      console.error('Error reenter phone number', error);
    }
  }


  const handleNext = async () => {
    if (!nameError || !emailError) {
      if (!firstName.trim()) {
        return validateFirstName();
      }
      if (!lastName.trim()) {
        return validateLastName();
      }
      if(!validPhoneNumber){
        return validatePhoneNumber();
      }
      if (!email.trim()) {
        return validateEmail();
      }
      localStorage.setItem('clientFirstName', firstName);
      localStorage.setItem('clientLastName', lastName);
      localStorage.setItem('clientPhoneNumber', phoneNumber);
      localStorage.setItem('clientEmail', email);
      if(enablePreOrder){
        try {
          setLoading(true)
          if(reenterPhone){
            return await updatePhone()
          }
          const response = await newSubscription({
            client_name: `${firstName} ${lastName}` || '',
            client_email: email || '',
            trainer_id: trainerData._id,
            client_phone: `+${phoneNumber}`,
            paypal_data: null,
          });
          const responseData = await response.json();

          if(responseData.subscription){
            localStorage.setItem('clientSubscriptionId', responseData.subscription._id);
          }
          
          setLoading(false)
          if (response.ok) {
            if (responseData.status === 'subscription completed') {
              window && (window as any).gtag && (window as any).gtag("event", "click", { send_to: `${process.env.GATSBY_AW_TRACKING_ID}/${process.env.GATSBY_SUBSCRIPTION_CONVERSION_LABEl}`, value: 200, currency: 'CAD' })
              return onNext({ firstName, lastName, phoneNumber, email });
            }
          }
          if (responseData.error === 'already subscribed') {
            setAlreadySubscribed(true)
            return onNext({ firstName, lastName, phoneNumber, email });
          }
          return setError(true);
        } catch (error) {
          setLoading(false)
          setError(true);
          console.error('Error free trial', error);
        }
      }else{
        onNext({ firstName, lastName, phoneNumber, email });
      }
    }
  };

  useEffect(() => {
    setName(localStorage.getItem('clientName') || '');
    setFirstName(localStorage.getItem('clientFirstName') || '')
    setLastName(localStorage.getItem('clientLastName') || '')
    setPhoneNumber(localStorage.getItem('clientPhoneNumber') || '')
    setEmail(localStorage.getItem('clientEmail') || '');
  }, []);

  const isValidHandler = (inputNumber:string, country:object, countries:object[]) => {
    
    const isValidCountry = (countries as Array<PhoneInputCountry>).some((country: PhoneInputCountry) => {
      return startsWith(inputNumber, country.dialCode) || startsWith(country.dialCode, inputNumber);
    });

    const isValidLength = inputNumber.length > 10

    setValidPhoneNumber(isValidCountry && isValidLength)
    return !phoneNumberError || (isValidCountry && isValidLength)
  }

  

  return (
    <Box className={classes.root}>
      <Box mt={3} />
      <TextField
        onChange={handleFirstNameChange}
        onBlur={validateFirstName}
        value={firstName}
        type="text"
        autoComplete="given-name"
        name="given-name"
        fullWidth
        label="First Name"
        hiddenLabel
        error={!!firstNameError}
        helperText={firstNameError}
        variant="outlined"
        disabled={reenterPhone}
      />
      <Box mt={3} />
      <TextField
        onChange={handleLastNameChange}
        onBlur={validateLastName}
        value={lastName}
        type="text"
        autoComplete="family-name"
        name="family-name"
        fullWidth
        label="Last Name"
        hiddenLabel
        error={!!lastNameError}
        helperText={lastNameError}
        variant="outlined"
        disabled={reenterPhone}
      />
      <Box mt={3} />
      <PhoneInput
        country={'ca'}
        value={phoneNumber}
        onChange={handlePhoneNumberChange}
        onBlur={validatePhoneNumber}
        autocompleteSearch
        specialLabel="Phone Number"
        inputProps={{autoComplete: 'tel', type: 'tel'}}
        countryCodeEditable={false}
        enableSearch
        jumpCursorToEnd
        isValid={isValidHandler}

      />
      {phoneNumberError && (<FormHelperText error variant="outlined">{phoneNumberError}</FormHelperText>)}
      <Box mt={3} />
      <TextField
        onChange={handleEmailChange}
        onBlur={validateEmail}
        value={email}
        type="email"
        name="email"
        autoComplete="email"
        fullWidth
        label="Email"
        hiddenLabel
        error={!!emailError}
        helperText={emailError}
        variant="outlined"
        disabled={reenterPhone}
      />
      <Box mt={5}>
        <Box position="relative" display="inline">
          <Button
            size="large"
            variant="contained"
            onClick={handleNext}
            color="primary"
            disabled={loading}
          >
            NEXT
          </Button>
          {loading && <CircularProgress size={24} className={classes.buttonProgress} />}
        </Box>
      </Box>
      <Snackbar
        open={error}
        autoHideDuration={6000}
        onClose={() => setError(false)}
      >
        <Alert onClose={() => setError(false)} severity="error">
          Something went wrong. Please try again later.
        </Alert>
      </Snackbar>
      <Snackbar
        open={alreadySubscribed}
        autoHideDuration={6000}
        onClose={() => setAlreadySubscribed(false)}
      >
        <Alert onClose={() => setAlreadySubscribed(false)} severity="info">
          You&apos;ve already subscribed!
        </Alert>
      </Snackbar>
    </Box>
  );
};
